/**
 * EmployeeLeaves — sub-tab on Talent (PMS) page.
 * Lets admins record rider/staff leave applications and produces a branded
 * PDF on save.
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API, getAuthHeader } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { SearchableSelect } from '../components/ui/searchable-select';
import { Plus, Download, FileText, Trash2, Eraser, CalendarDays, MoreVertical } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const REASON_OPTIONS = ['Annual Leave', 'Emergency leave', 'Others'];

const todayStr = () => {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
};

const daysBetween = (s, e) => {
  if (!s || !e) return 0;
  const a = new Date(s), b = new Date(e);
  return Math.max(0, Math.round((b - a) / 86400000) + 1);
};

// Trigger a file download from a Blob.
const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  window.URL.revokeObjectURL(url);
};

// ---------------------------------------------------------------------------
// Touchpad signature canvas — native HTML5 canvas, no external libs
// ---------------------------------------------------------------------------

const SignaturePad = React.forwardRef(({ width = 320, height = 90, label = 'Sign here', testid = 'sig' }, ref) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  const getPoint = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - r.left, y: t.clientY - r.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    last.current = getPoint(e);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const c = canvasRef.current.getContext('2d');
    const p = getPoint(e);
    c.strokeStyle = '#111'; c.lineWidth = 2; c.lineCap = 'round';
    c.beginPath();
    c.moveTo(last.current.x, last.current.y);
    c.lineTo(p.x, p.y);
    c.stroke();
    last.current = p;
  };
  const end = () => { drawing.current = false; };

  const clear = useCallback(() => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, c.width, c.height);
  }, []);

  const toDataURL = useCallback(() => {
    const c = canvasRef.current; if (!c) return null;
    // Check if canvas is actually drawn on
    const ctx = c.getContext('2d');
    const data = ctx.getImageData(0, 0, c.width, c.height).data;
    let blank = true;
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] !== 0) { blank = false; break; }
    }
    if (blank) return null;
    return c.toDataURL('image/png');
  }, []);

  // Expose methods to parent
  React.useImperativeHandle(ref, () => ({ clear, toDataURL }), [clear, toDataURL]);

  return (
    <div className="border border-slate-300 rounded bg-white">
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        className="block cursor-crosshair touch-none"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        data-testid={`${testid}-canvas`}
      />
      <div className="flex items-center justify-between text-[10px] text-slate-500 px-2 py-1 border-t border-slate-200 bg-slate-50">
        <span>{label}</span>
        <button type="button" className="flex items-center gap-1 hover:text-slate-700" onClick={clear} data-testid={`${testid}-clear`}>
          <Eraser className="w-3 h-3" /> Clear
        </button>
      </div>
    </div>
  );
});
SignaturePad.displayName = 'SignaturePad';

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const EmployeeLeaves = () => {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [riders, setRiders] = useState([]);
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  // Reject + Details dialogs
  const [rejectTarget, setRejectTarget] = useState(null); // leave object being rejected
  const [rejectReason, setRejectReason] = useState('');
  const [rejectSubmitting, setRejectSubmitting] = useState(false);
  const [detailsLeave, setDetailsLeave] = useState(null); // leave object whose details are open
  // Current user (for approve/reject permission check)
  const [currentUser, setCurrentUser] = useState(null);
  useEffect(() => {
    axios.get(`${API}/auth/me`, { headers: getAuthHeader() })
      .then(r => setCurrentUser(r.data))
      .catch(() => {});
  }, []);
  const canApprove = useMemo(() => {
    if (!currentUser) return false;
    const role = (currentUser.role || '').toLowerCase().replace(/[_\s]/g, '');
    if (['admin', 'superadmin'].includes(role)) return true;
    return !!currentUser?.permissions?.leaves?.approve;
  }, [currentUser]);

  const approveLeave = async (id) => {
    try {
      const res = await axios.post(`${API}/talent/leaves/${id}/approve`, {}, { headers: getAuthHeader() });
      setLeaves(prev => prev.map(l => l.id === id ? res.data.leave : l));
      toast.success('Leave approved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approve failed');
    }
  };
  const submitReject = async () => {
    if (!rejectTarget) return;
    if (!rejectReason.trim()) { toast.error('Please enter a reason for rejection.'); return; }
    try {
      setRejectSubmitting(true);
      const res = await axios.post(
        `${API}/talent/leaves/${rejectTarget.id}/reject`,
        { reason: rejectReason.trim() },
        { headers: getAuthHeader() },
      );
      setLeaves(prev => prev.map(l => l.id === rejectTarget.id ? res.data.leave : l));
      toast.success('Leave rejected');
      setRejectTarget(null); setRejectReason('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reject failed');
    } finally { setRejectSubmitting(false); }
  };

  // ----- Initial data ---------------------------------------------------------
  const fetchAll = async () => {
    try {
      setLoading(true);
      const [leavesRes, ridersRes, staffRes] = await Promise.all([
        axios.get(`${API}/talent/leaves`, { headers: getAuthHeader() }),
        axios.get(`${API}/riders/dropdown`, { headers: getAuthHeader() }),
        axios.get(`${API}/talent/employees?employee_type=staff`, { headers: getAuthHeader() }),
      ]);
      setLeaves(leavesRes.data?.leaves || []);
      setRiders(ridersRes.data?.riders || ridersRes.data || []);
      const allEmps = staffRes.data?.employees || staffRes.data || [];
      setStaff(allEmps.filter(e =>
        (e.employee_type || '').toLowerCase() === 'staff' || (e.type || '').toLowerCase() === 'staff'
      ));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load leaves');
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  // ----- Download an existing leave PDF --------------------------------------
  const downloadPdf = async (leave) => {
    try {
      const res = await axios.get(`${API}/talent/leaves/${leave.id}/pdf`, { headers: getAuthHeader(), responseType: 'blob' });
      const safe = (leave.employee_name || 'leave').replace(/\s/g, '_').substring(0, 25);
      downloadBlob(new Blob([res.data], { type: 'application/pdf' }), `Leave_${leave.leave_unique_id}_${safe}.pdf`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to download PDF');
    }
  };

  const deleteLeave = async (id) => {
    if (!window.confirm('Delete this leave entry?')) return;
    try {
      await axios.delete(`${API}/talent/leaves/${id}`, { headers: getAuthHeader() });
      setLeaves(prev => prev.filter(l => l.id !== id));
      toast.success('Leave deleted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  // ----- Filtered log rows ----------------------------------------------------
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return leaves;
    return leaves.filter(l =>
      [l.employee_name, l.employee_unique_id, l.emirates_id, l.platform, l.platform_id, l.leave_unique_id]
        .some(v => String(v || '').toLowerCase().includes(q))
    );
  }, [leaves, search]);

  return (
    <div className="space-y-4" data-testid="employee-leaves-page">
      <Card>
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <Button
            onClick={() => setOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 gap-2"
            data-testid="add-leave-entry-btn"
          >
            <Plus className="w-4 h-4" /> Add Leave Entry
          </Button>
          <Input
            placeholder="Search by name / ID / Emirates ID / platform…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="ml-auto w-72"
            data-testid="leaves-search-input"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-blue-600" /> Employee Leaves Status Log ({rows.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-auto">
          <Table className="min-w-[1200px]">
            <TableHeader>
              <TableRow className="text-[11px] uppercase tracking-wide text-slate-600">
                <TableHead>Unique ID</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Emirates ID</TableHead>
                <TableHead>Platform</TableHead>
                <TableHead>Platform ID</TableHead>
                <TableHead>Leave Start</TableHead>
                <TableHead>Leave End</TableHead>
                <TableHead className="text-center">Days</TableHead>
                <TableHead>Submitted By</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="text-center w-10"> </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={12} className="text-center py-8 text-slate-400">Loading…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={12} className="text-center py-8 text-slate-400">No leave entries yet.</TableCell></TableRow>
              ) : rows.map(l => {
                const statusPill = {
                  pending_approval: 'bg-amber-50 text-amber-700 border-amber-300',
                  approved: 'bg-green-50 text-green-700 border-green-300',
                  rejected: 'bg-red-50 text-red-700 border-red-300',
                }[l.status] || 'bg-slate-50 text-slate-700 border-slate-300';
                const statusLabel = {
                  pending_approval: 'Pending', approved: 'Approved', rejected: 'Rejected',
                }[l.status] || 'Pending';
                return (
                <TableRow key={l.id} className="text-xs cursor-pointer hover:bg-slate-50" onClick={() => setDetailsLeave(l)} data-testid={`leave-row-${l.id}`}>
                  <TableCell className="font-mono">{l.leave_unique_id || l.id?.slice(0, 8)}</TableCell>
                  <TableCell><Badge variant="outline" className={l.employee_category === 'Rider' ? 'bg-green-50 text-green-700' : 'bg-purple-50 text-purple-700'}>{l.employee_category}</Badge></TableCell>
                  <TableCell>
                    <p className="font-medium">{l.employee_name}</p>
                    <p className="text-[10px] text-slate-500 font-mono">{l.employee_unique_id}</p>
                  </TableCell>
                  <TableCell className="font-mono">{l.emirates_id || '—'}</TableCell>
                  <TableCell>{l.platform || '—'}</TableCell>
                  <TableCell className="font-mono">{l.platform_id || '—'}</TableCell>
                  <TableCell>{l.leave_start_date}</TableCell>
                  <TableCell>{l.leave_end_date}</TableCell>
                  <TableCell className="text-center font-semibold text-blue-700">{l.leave_days}</TableCell>
                  <TableCell>{l.created_by_name || l.created_by || '—'}</TableCell>
                  <TableCell className="text-center"><Badge variant="outline" className={statusPill}>{statusLabel}</Badge></TableCell>
                  <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" data-testid={`leave-actions-${l.id}`}><MoreVertical className="w-4 h-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {l.status === 'pending_approval' && canApprove && (
                          <>
                            <DropdownMenuItem onClick={() => approveLeave(l.id)} className="text-green-700" data-testid={`approve-leave-${l.id}`}>
                              ✓ Approve
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => { setRejectTarget(l); setRejectReason(''); }} className="text-amber-700" data-testid={`reject-leave-${l.id}`}>
                              ✕ Reject
                            </DropdownMenuItem>
                          </>
                        )}
                        <DropdownMenuItem onClick={() => downloadPdf(l)} className="text-blue-700" data-testid={`pdf-leave-${l.id}`}>
                          <Download className="w-3.5 h-3.5 mr-2" /> PDF
                        </DropdownMenuItem>
                        <DropdownMenuItem className="text-red-600 focus:text-red-700 focus:bg-red-50" onClick={() => deleteLeave(l.id)} data-testid={`delete-leave-${l.id}`}>
                          <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );})}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <AddLeaveDialog
        open={open}
        onOpenChange={setOpen}
        riders={riders}
        staff={staff}
        onDone={(newLeave) => {
          setLeaves(prev => [newLeave, ...prev]);
        }}
      />

      {/* Reject Reason Dialog */}
      <Dialog open={!!rejectTarget} onOpenChange={(o) => { if (!o) { setRejectTarget(null); setRejectReason(''); } }}>
        <DialogContent className="max-w-md" data-testid="reject-leave-dialog">
          <DialogHeader>
            <DialogTitle>Reject Leave — {rejectTarget?.leave_unique_id}</DialogTitle>
            <DialogDescription>
              {rejectTarget?.employee_name} · {rejectTarget?.leave_start_date} → {rejectTarget?.leave_end_date}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label className="text-xs">Reason for rejection <span className="text-red-500">*</span></Label>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Describe why this leave is being rejected…"
              rows={4}
              data-testid="reject-reason-textarea"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejectTarget(null); setRejectReason(''); }}>Cancel</Button>
            <Button
              onClick={submitReject}
              disabled={rejectSubmitting || !rejectReason.trim()}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="reject-confirm-btn"
            >
              {rejectSubmitting ? 'Rejecting…' : 'Confirm Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Leave Details Dialog */}
      <LeaveDetailsDialog leave={detailsLeave} onClose={() => setDetailsLeave(null)} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Add Leave Dialog — big form with auto-fill, air ticket, signatures
// ---------------------------------------------------------------------------

function AddLeaveDialog({ open, onOpenChange, riders, staff, onDone }) {
  const [empType, setEmpType] = useState('rider'); // 'rider' | 'staff'
  const [employeeId, setEmployeeId] = useState('');
  const [profile, setProfile] = useState(null);
  const [applicationDate, setApplicationDate] = useState(todayStr());
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reasonType, setReasonType] = useState('Annual Leave');
  const [reasonDetails, setReasonDetails] = useState('');
  const [air, setAir] = useState({ pnr: '', airline: '', departure_date: '', departure_time: '', arrival_date: '', arrival_time: '' });
  const [homeCountry, setHomeCountry] = useState('+');
  const [homeMobile, setHomeMobile] = useState('');
  const [homeAddress, setHomeAddress] = useState('');
  const [ref, setRef] = useState({ name: '', relation: '', country_code: '+', mobile: '' });
  const [approverId, setApproverId] = useState('');
  const [prereqs, setPrereqs] = useState(() => ({
    cod: { status: 'fully_paid', amount: '', receipt_url: '' },
    sim: { status: 'returned' },
    inventory: { status: 'returned' },
    bike: { status: 'returned' },
    fines: { status: 'fully_paid', amount: '', receipt_url: '', outstanding_at_filing: 0 },
    salik: { status: 'fully_paid', amount: '', receipt_url: '' },
  }));
  const [submitting, setSubmitting] = useState(false);

  const applicantSigRef = useRef(null);
  const approverSigRef = useRef(null);

  useEffect(() => {
    if (open) {
      // Reset all state
      setEmpType('rider'); setEmployeeId(''); setProfile(null);
      setApplicationDate(todayStr()); setStartDate(''); setEndDate('');
      setReasonType('Annual Leave'); setReasonDetails('');
      setAir({ pnr: '', airline: '', departure_date: '', departure_time: '', arrival_date: '', arrival_time: '' });
      setHomeCountry('+'); setHomeMobile(''); setHomeAddress('');
      setRef({ name: '', relation: '', country_code: '+', mobile: '' });
      setApproverId('');
      setPrereqs({
        cod: { status: 'fully_paid', amount: '', receipt_url: '' },
        sim: { status: 'returned' },
        inventory: { status: 'returned' },
        bike: { status: 'returned' },
        fines: { status: 'fully_paid', amount: '', receipt_url: '', outstanding_at_filing: 0 },
        salik: { status: 'fully_paid', amount: '', receipt_url: '' },
      });
    }
  }, [open]);

  // Employees options
  const empOptions = useMemo(() => {
    const list = empType === 'rider' ? riders : staff;
    return list.map(p => ({
      value: p.id,
      label: p.name || p.full_name || '—',
      sublabel: p.rider_unique_id || p.employee_unique_id || p.unique_id || '',
    }));
  }, [empType, riders, staff]);

  const staffOptions = useMemo(() => staff.map(s => ({ value: s.id, label: s.name || s.full_name || '—' })), [staff]);

  // ----- Auto-fill on employee select ----------------------------------------
  useEffect(() => {
    if (!employeeId) { setProfile(null); return; }
    axios.get(`${API}/talent/leaves/employee-profile/${employeeId}`, { headers: getAuthHeader() })
      .then(res => setProfile(res.data))
      .catch(e => {
        setProfile(null);
        toast.error(e.response?.data?.detail || 'Failed to load profile');
      });
  }, [employeeId]);

  const days = daysBetween(startDate, endDate);

  // ----- Submit ---------------------------------------------------------------
  const handleSubmit = async () => {
    if (!employeeId || !startDate || !endDate || !reasonType) {
      toast.error('Employee, start date, end date and reason are required.'); return;
    }
    if (new Date(endDate) < new Date(startDate)) {
      toast.error('End date must be on/after the start date.'); return;
    }
    if (reasonType !== 'Annual Leave' && !reasonDetails.trim()) {
      toast.error('Please describe the reason.'); return;
    }
    const approver = staff.find(s => s.id === approverId);
    try {
      setSubmitting(true);
      const body = {
        employee_id: employeeId,
        employee_name: profile?.name,
        employee_unique_id: profile?.employee_unique_id,
        employee_category: profile?.employee_category,
        emirates_id: profile?.emirates_id,
        emirates_id_expiry: profile?.emirates_id_expiry,
        passport_number: profile?.passport_number,
        passport_issue_date: profile?.passport_issue_date,
        passport_expiry: profile?.passport_expiry,
        nationality: profile?.nationality,
        visa_expiry_date: profile?.visa_expiry_date,
        platform: profile?.platform,
        platform_id: profile?.platform_id,
        mobile_number: profile?.mobile_number,
        bike_plate: profile?.bike_plate,
        loan_outstanding: profile?.loan_outstanding || 0,
        application_date: applicationDate,
        leave_start_date: startDate,
        leave_end_date: endDate,
        leave_days: days,
        reason_type: reasonType,
        reason_details: reasonDetails,
        air_ticket: air,
        home_contact_country: homeCountry,
        home_contact_mobile: homeMobile,
        home_address: homeAddress,
        reference_contact: ref,
        approver_id: approverId || null,
        approver_name: approver?.name || approver?.full_name || null,
        status: 'pending_approval',
        leave_prerequisites: prereqs,
      };
      const res = await axios.post(`${API}/talent/leaves`, body, { headers: getAuthHeader() });
      toast.success('Leave submitted for approval.');
      onOpenChange(false);
      onDone?.(res.data.leave);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="add-leave-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarDays className="w-5 h-5 text-blue-600" /> Add Leave Entry</DialogTitle>
          <DialogDescription>The PDF will auto-download once you save.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          {/* Employee selector */}
          <Card className="bg-slate-50/50">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-3">
                <Label className="text-xs">Employee Category:</Label>
                {['rider', 'staff'].map(t => (
                  <Button
                    key={t}
                    type="button"
                    size="sm"
                    variant={empType === t ? 'default' : 'outline'}
                    onClick={() => { setEmpType(t); setEmployeeId(''); }}
                    className={empType === t ? 'bg-blue-600 hover:bg-blue-700' : ''}
                    data-testid={`leave-emp-type-${t}`}
                  >{t === 'rider' ? 'Rider' : 'Staff'}</Button>
                ))}
              </div>
              <SearchableSelect
                value={employeeId}
                onValueChange={setEmployeeId}
                options={empOptions}
                placeholder={`Search ${empType} by name or ID…`}
                data-testid="leave-employee-select"
              />
              {profile && (
                <div className="grid grid-cols-3 gap-3 text-xs pt-2 mt-2 border-t">
                  <div><span className="text-slate-500">Unique ID:</span><br /><span className="font-mono">{profile.employee_unique_id || '—'}</span></div>
                  <div><span className="text-slate-500">Emirates ID:</span><br /><span className="font-mono">{profile.emirates_id || '—'}</span></div>
                  <div><span className="text-slate-500">EID Expiry:</span><br /><span className="font-mono">{profile.emirates_id_expiry || '—'}</span></div>
                  <div><span className="text-slate-500">Passport:</span><br /><span className="font-mono">{profile.passport_number || '—'}</span></div>
                  <div><span className="text-slate-500">Passport Issue:</span><br /><span className="font-mono">{profile.passport_issue_date || '—'}</span></div>
                  <div><span className="text-slate-500">Passport Expiry:</span><br /><span className="font-mono">{profile.passport_expiry || '—'}</span></div>
                  <div><span className="text-slate-500">Nationality:</span><br />{profile.nationality || '—'}</div>
                  <div><span className="text-slate-500">Visa Expiry:</span><br /><span className="font-mono">{profile.visa_expiry_date || '—'}</span></div>
                  <div><span className="text-slate-500">Platform:</span><br />{profile.platform || '—'}</div>
                  <div><span className="text-slate-500">Platform ID:</span><br /><span className="font-mono">{profile.platform_id || '—'}</span></div>
                  <div><span className="text-slate-500">Mobile (from SIM):</span><br /><span className="font-mono">{profile.mobile_number || '—'}</span></div>
                  <div><span className="text-slate-500">Bike Plate:</span><br /><span className="font-mono">{profile.bike_plate || '—'}</span></div>
                  <div className="col-span-3"><span className="text-slate-500">Loan Outstanding:</span> <span className="font-mono text-red-600 font-semibold">AED {Number(profile.loan_outstanding || 0).toFixed(2)}</span></div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Leave dates */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Application Date</Label>
              <Input type="date" value={applicationDate} onChange={(e) => setApplicationDate(e.target.value)} data-testid="leave-app-date" />
            </div>
            <div>
              <Label className="text-xs">Leave Start <span className="text-red-500">*</span></Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="leave-start-date" />
            </div>
            <div>
              <Label className="text-xs">Leave End <span className="text-red-500">*</span></Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="leave-end-date" />
            </div>
          </div>
          {days > 0 && (
            <p className="text-xs text-slate-600">Total leave days: <strong className="text-blue-700">{days}</strong></p>
          )}

          {/* Reason */}
          <div>
            <Label className="text-xs">Reason for Leave <span className="text-red-500">*</span></Label>
            <Select value={reasonType} onValueChange={setReasonType}>
              <SelectTrigger data-testid="leave-reason-select"><SelectValue /></SelectTrigger>
              <SelectContent>{REASON_OPTIONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
            {reasonType !== 'Annual Leave' && (
              <Textarea
                placeholder="Describe the reason…"
                value={reasonDetails}
                onChange={(e) => setReasonDetails(e.target.value)}
                className="mt-2"
                data-testid="leave-reason-details"
              />
            )}
          </div>

          {/* Air ticket */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Air Ticket Details (optional)</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">PNR</Label><Input value={air.pnr} onChange={(e) => setAir(a => ({ ...a, pnr: e.target.value }))} /></div>
              <div className="col-span-2"><Label className="text-xs">Airline Name</Label><Input value={air.airline} onChange={(e) => setAir(a => ({ ...a, airline: e.target.value }))} /></div>
              <div><Label className="text-xs">Departure Date</Label><Input type="date" value={air.departure_date} onChange={(e) => setAir(a => ({ ...a, departure_date: e.target.value }))} /></div>
              <div><Label className="text-xs">Departure Time</Label><Input type="time" value={air.departure_time} onChange={(e) => setAir(a => ({ ...a, departure_time: e.target.value }))} /></div>
              <div></div>
              <div><Label className="text-xs">Arrival Date</Label><Input type="date" value={air.arrival_date} onChange={(e) => setAir(a => ({ ...a, arrival_date: e.target.value }))} /></div>
              <div><Label className="text-xs">Arrival Time</Label><Input type="time" value={air.arrival_time} onChange={(e) => setAir(a => ({ ...a, arrival_time: e.target.value }))} /></div>
            </CardContent>
          </Card>

          {/* Home contact + address */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Home Contact</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs">Country Code</Label>
                  <Input value={homeCountry} onChange={(e) => setHomeCountry(e.target.value)} placeholder="+91" />
                </div>
                <div className="col-span-3">
                  <Label className="text-xs">Mobile No.</Label>
                  <Input value={homeMobile} onChange={(e) => setHomeMobile(e.target.value)} />
                </div>
              </div>
              <div>
                <Label className="text-xs">Home Address</Label>
                <Textarea value={homeAddress} onChange={(e) => setHomeAddress(e.target.value)} placeholder="Full address while on leave…" />
              </div>
            </CardContent>
          </Card>

          {/* Reference contact */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Home Reference Contact Person</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-4 gap-3">
              <div><Label className="text-xs">Name</Label><Input value={ref.name} onChange={(e) => setRef(r => ({ ...r, name: e.target.value }))} /></div>
              <div><Label className="text-xs">Relation</Label><Input value={ref.relation} onChange={(e) => setRef(r => ({ ...r, relation: e.target.value }))} placeholder="e.g. Father" /></div>
              <div><Label className="text-xs">Country Code</Label><Input value={ref.country_code} onChange={(e) => setRef(r => ({ ...r, country_code: e.target.value }))} placeholder="+91" /></div>
              <div><Label className="text-xs">Mobile</Label><Input value={ref.mobile} onChange={(e) => setRef(r => ({ ...r, mobile: e.target.value }))} /></div>
            </CardContent>
          </Card>

          {/* Leave Prerequisites — clearance checks before granting leave */}
          <PrerequisitesCard
            value={prereqs}
            onChange={setPrereqs}
            employeeId={employeeId}
          />

          {/* Approver */}
          <div>
            <Label className="text-xs">Approver Name (Staff)</Label>
            <SearchableSelect
              value={approverId}
              onValueChange={setApproverId}
              options={staffOptions}
              placeholder="Select staff approver…"
              data-testid="leave-approver-select"
            />
          </div>
        </div>

        <DialogFooter className="pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-blue-600 hover:bg-blue-700 gap-2"
            data-testid="leave-submit-btn"
          >
            <FileText className="w-4 h-4" /> {submitting ? 'Submitting…' : 'Submit for Approval'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default EmployeeLeaves;

// ---------------------------------------------------------------------------
// Leave Details Dialog — read-only view of a leave application
// ---------------------------------------------------------------------------

const DetailsSection = ({ title, children }) => (
  <Card>
    <CardHeader className="pb-2"><CardTitle className="text-sm">{title}</CardTitle></CardHeader>
    <CardContent className="grid grid-cols-3 gap-3 text-xs">{children}</CardContent>
  </Card>
);

const DetailsField = ({ label, value, mono, full }) => (
  <div className={full ? 'col-span-3' : ''}>
    <p className="text-slate-500 text-[10px] uppercase">{label}</p>
    <p className={`${mono ? 'font-mono' : ''} ${!value && value !== 0 ? 'text-slate-400' : ''}`}>{value || value === 0 ? value : '—'}</p>
  </div>
);

function LeaveDetailsDialog({ leave, onClose }) {
  if (!leave) return null;

  const statusPill = {
    pending_approval: { bg: 'bg-amber-50 text-amber-700 border-amber-300', label: 'Pending' },
    approved: { bg: 'bg-green-50 text-green-700 border-green-300', label: 'Approved' },
    rejected: { bg: 'bg-red-50 text-red-700 border-red-300', label: 'Rejected' },
  }[leave.status] || { bg: 'bg-slate-50 text-slate-700 border-slate-300', label: 'Pending' };

  const air = leave.air_ticket || {};
  const refc = leave.reference_contact || {};
  const pr = leave.leave_prerequisites || {};
  const prLabel = (s) => ({ fully_paid: 'Fully Paid', outstanding: 'Outstanding', returned: 'Returned', not_returned: 'Not Returned' }[s] || s || '—');
  const prColor = (s) => s === 'fully_paid' || s === 'returned' ? 'text-green-700' : s === 'outstanding' || s === 'not_returned' ? 'text-red-700' : '';

  return (
    <Dialog open={!!leave} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="leave-details-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <CalendarDays className="w-5 h-5 text-blue-600" />
            Leave Application — <span className="font-mono">{leave.leave_unique_id}</span>
            <Badge variant="outline" className={statusPill.bg}>{statusPill.label}</Badge>
          </DialogTitle>
          <DialogDescription>
            Submitted by <strong>{leave.created_by_name || leave.created_by || '—'}</strong>
            {leave.created_at ? ` on ${new Date(leave.created_at).toLocaleString()}` : ''}
          </DialogDescription>
        </DialogHeader>

        {leave.status === 'rejected' && leave.rejection_reason && (
          <div className="bg-red-50 border border-red-200 rounded p-3 text-xs">
            <p className="font-semibold text-red-800 mb-1">Rejection Reason</p>
            <p className="text-red-700 whitespace-pre-wrap">{leave.rejection_reason}</p>
            <p className="text-[10px] text-red-600 mt-2">
              Rejected by {leave.approved_by_name || '—'}
              {leave.approved_at ? ` on ${new Date(leave.approved_at).toLocaleString()}` : ''}
            </p>
          </div>
        )}
        {leave.status === 'approved' && (
          <div className="bg-green-50 border border-green-200 rounded p-3 text-xs text-green-700">
            Approved by <strong>{leave.approved_by_name || '—'}</strong>
            {leave.approved_at ? ` on ${new Date(leave.approved_at).toLocaleString()}` : ''}
          </div>
        )}

        <div className="space-y-4 pt-2">
          <DetailsSection title="Employee">
            <DetailsField label="Category" value={leave.employee_category} />
            <DetailsField label="Name" value={leave.employee_name} />
            <DetailsField label="Unique ID" value={leave.employee_unique_id} mono />
            <DetailsField label="Emirates ID" value={leave.emirates_id} mono />
            <DetailsField label="EID Expiry" value={leave.emirates_id_expiry} mono />
            <DetailsField label="Passport" value={leave.passport_number} mono />
            <DetailsField label="Passport Issue" value={leave.passport_issue_date} mono />
            <DetailsField label="Passport Expiry" value={leave.passport_expiry} mono />
            <DetailsField label="Nationality" value={leave.nationality} />
            <DetailsField label="Visa Expiry" value={leave.visa_expiry_date} mono />
            <DetailsField label="Platform" value={leave.platform} />
            <DetailsField label="Platform ID" value={leave.platform_id} mono />
            <DetailsField label="Mobile (SIM)" value={leave.mobile_number} mono />
            <DetailsField label="Bike Plate" value={leave.bike_plate} mono />
            <DetailsField label="Loan Outstanding" value={leave.loan_outstanding != null ? `AED ${Number(leave.loan_outstanding).toFixed(2)}` : '—'} />
          </DetailsSection>

          <DetailsSection title="Leave Period">
            <DetailsField label="Application Date" value={leave.application_date} />
            <DetailsField label="Leave Start" value={leave.leave_start_date} />
            <DetailsField label="Leave End" value={leave.leave_end_date} />
            <DetailsField label="Total Days" value={leave.leave_days} />
            <DetailsField label="Reason" value={leave.reason_type} />
            <DetailsField label="Details" value={leave.reason_details} full />
          </DetailsSection>

          <DetailsSection title="Air Ticket">
            <DetailsField label="PNR" value={air.pnr} mono />
            <DetailsField label="Airline" value={air.airline} />
            <DetailsField label="Departure" value={air.departure_date ? `${air.departure_date} ${air.departure_time || ''}`.trim() : '—'} />
            <DetailsField label="Arrival" value={air.arrival_date ? `${air.arrival_date} ${air.arrival_time || ''}`.trim() : '—'} />
          </DetailsSection>

          <DetailsSection title="Home Contact">
            <DetailsField label="Country Code" value={leave.home_contact_country} />
            <DetailsField label="Mobile" value={leave.home_contact_mobile} mono />
            <DetailsField label="Home Address" value={leave.home_address} full />
          </DetailsSection>

          <DetailsSection title="Reference Contact Person">
            <DetailsField label="Name" value={refc.name} />
            <DetailsField label="Relation" value={refc.relation} />
            <DetailsField label="Country Code" value={refc.country_code} />
            <DetailsField label="Mobile" value={refc.mobile} mono />
          </DetailsSection>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Leave Pre-requisites</CardTitle></CardHeader>
            <CardContent className="text-xs">
              <table className="w-full">
                <tbody>
                  {[
                    ['COD', 'cod'], ['SIM Card', 'sim'], ['Inventory', 'inventory'],
                    ['Bike', 'bike'], ['Fines', 'fines'], ['SALIK', 'salik'],
                  ].map(([label, k]) => {
                    const row = pr[k] || {};
                    return (
                      <tr key={k} className="border-b last:border-b-0">
                        <td className="py-1.5 font-medium w-40">{label}</td>
                        <td className={`py-1.5 ${prColor(row.status)}`}>{prLabel(row.status)}</td>
                        <td className="py-1.5 text-slate-600">
                          {row.amount != null && row.amount !== '' && <span>AED {Number(row.amount).toFixed(2)}</span>}
                          {row.receipt_url && (
                            <a href={row.receipt_url} target="_blank" rel="noreferrer" className="text-blue-600 underline ml-2">
                              View Receipt
                            </a>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Prerequisites card — clearance checks before granting leave
// ---------------------------------------------------------------------------

// Returned / Not Returned dropdown row (SIM, Inventory, Bike)
const ReturnRow = ({ label, k, value, update }) => {
  const v = value[k] || {};
  const statusColor = v.status === 'returned' ? 'text-green-700' : v.status === 'not_returned' ? 'text-red-700' : '';
  return (
    <div className="grid grid-cols-3 gap-3 items-center py-2 border-b last:border-b-0">
      <span className="text-xs font-medium">{label}</span>
      <div className="col-span-2">
        <Select value={v.status || ''} onValueChange={(val) => update(k, { status: val })}>
          <SelectTrigger className={`h-8 text-xs ${statusColor}`} data-testid={`prereq-${k}-select`}>
            <SelectValue placeholder="Select status…" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="returned">Returned</SelectItem>
            <SelectItem value="not_returned">Not Returned</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

// Fully Paid / Outstanding dropdown row (COD, Fines, SALIK)
const PaidRow = ({ label, k, value, update, onUploadReceipt, autoOutstanding = null }) => {
  const v = value[k] || {};
  const statusColor = v.status === 'fully_paid' ? 'text-green-700' : v.status === 'outstanding' ? 'text-amber-700' : '';
  return (
    <div className="py-2 border-b last:border-b-0">
      <div className="grid grid-cols-3 gap-3 items-center">
        <span className="text-xs font-medium">
          {label}
          {autoOutstanding !== null && autoOutstanding > 0 && (
            <span className="ml-2 text-[10px] text-red-600 font-normal">(System: AED {autoOutstanding.toFixed(2)})</span>
          )}
        </span>
        <div className="col-span-2">
          <Select value={v.status || ''} onValueChange={(val) => update(k, { status: val })}>
            <SelectTrigger className={`h-8 text-xs ${statusColor}`} data-testid={`prereq-${k}-select`}>
              <SelectValue placeholder="Select status…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="fully_paid">Fully Paid</SelectItem>
              <SelectItem value="outstanding">Outstanding</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {v.status === 'fully_paid' && (
        <div className="mt-2 pl-1">
          <Label className="text-[10px] text-slate-600">Receipt (PDF/Image)</Label>
          <Input type="file" accept="application/pdf,image/*" className="h-8 text-xs"
            onChange={(e) => onUploadReceipt(k, e.target.files?.[0])}
            data-testid={`prereq-${k}-receipt`}
          />
          {v.receipt_url && <p className="text-[10px] text-green-600 mt-1">Uploaded ✓</p>}
        </div>
      )}
      {v.status === 'outstanding' && (
        <div className="mt-2 pl-1">
          <Label className="text-[10px] text-slate-600">Outstanding Amount (AED)</Label>
          <Input type="number" step="0.01" value={v.amount || ''}
            onChange={(e) => update(k, { amount: e.target.value })}
            className="h-8 text-xs" placeholder="0.00"
            data-testid={`prereq-${k}-amount`}
          />
        </div>
      )}
    </div>
  );
};

function PrerequisitesCard({ value, onChange, employeeId }) {
  // Auto-fetch outstanding fines for the selected employee
  useEffect(() => {
    if (!employeeId) return;
    axios.get(`${API}/talent/leaves/employee-outstanding/${employeeId}`, { headers: getAuthHeader() })
      .then(res => {
        const outstanding = Number(res.data?.outstanding_fines || 0);
        onChange(prev => ({
          ...prev,
          fines: {
            ...prev.fines,
            outstanding_at_filing: outstanding,
            status: outstanding > 0 ? 'outstanding' : 'fully_paid',
            amount: outstanding > 0 ? outstanding.toFixed(2) : '',
          },
        }));
      })
      .catch(() => { /* silent */ });
  }, [employeeId]);

  const update = (key, patch) => onChange(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  const handleReceiptUpload = async (key, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', `leave-receipts/${key}`);
      const res = await axios.post(`${API}/uploads`, fd, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
      });
      update(key, { receipt_url: res.data?.url || res.data?.file_url || '' });
      toast.success(`${key.toUpperCase()} receipt uploaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <FileText className="w-4 h-4 text-blue-600" /> Leave Pre-requisites
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1">
        <PaidRow label="COD" k="cod" value={value} update={update} onUploadReceipt={handleReceiptUpload} />
        <ReturnRow label="SIM Card" k="sim" value={value} update={update} />
        <ReturnRow label="Inventory" k="inventory" value={value} update={update} />
        <ReturnRow label="Bike" k="bike" value={value} update={update} />
        <PaidRow label="Fines" k="fines" value={value} update={update} onUploadReceipt={handleReceiptUpload} autoOutstanding={value.fines?.outstanding_at_filing || 0} />
        <PaidRow label="SALIK" k="salik" value={value} update={update} onUploadReceipt={handleReceiptUpload} />
      </CardContent>
    </Card>
  );
}
